import type { SensorAPI } from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration'
import type React from 'react'
import { scrollIntoView } from '../_utils/scrollIntoView'
import type { Selection } from './MenuImperative.types'

function noop() {}

const MOUSE_ENTER = 'mouseenter'
const MOUSE_LEAVE = 'mouseleave'

let lastDocumentInputType: InputModality = 'mouse'

if (typeof document !== 'undefined') {
  document.addEventListener(
    'keydown',
    () => {
      lastDocumentInputType = 'keyboard'
    },
    true
  )
  document.addEventListener(
    'pointerdown',
    () => {
      lastDocumentInputType = 'mouse'
    },
    true
  )
}

const menuItemsWrapperAttributeName = 'data-core-menuimperative-items-wrapper'
export const menuItemsWrapperAttribute = {
  [menuItemsWrapperAttributeName]: 'true',
}

const isItem = (element: Element | null) => element?.hasAttribute('data-value')

function isGroup(el: HTMLElement) {
  return JSON.parse(el.dataset.group || 'false')
}

const isWrapper = (element: Element | null) =>
  element?.getAttribute(menuItemsWrapperAttributeName) === 'true'

function getAllItems(menu: Element | null) {
  return menu?.querySelectorAll<HTMLElement>(
    '[data-value]:not([data-group="true"])'
  )
}

function getFirstItem(menu: Element | null) {
  return getAllItems(menu)?.[0]
}

function getPrevItem(item: Element, menu: Element | null): HTMLElement | null {
  const items = getAllItems(menu)

  return items ? items[Array.prototype.indexOf.call(items, item) - 1] : null
}

function getNextItem(item: Element, menu: Element | null): HTMLElement | null {
  const items = getAllItems(menu)

  return items ? items[Array.prototype.indexOf.call(items, item) + 1] : null
}

function getLastItem(menu: Element | null) {
  const items = getAllItems(menu)
  return items?.[items.length - 1]
}

type InputModality = 'mouse' | 'keyboard'

export function createSensors(
  ref: React.RefObject<HTMLDivElement>,
  circular: boolean,
  onChangeActiveDescendant: (
    id: string | undefined,
    menuRef: Element | null
  ) => void
) {
  let highlightedItem: HTMLElement | null = null
  let dragAndDropApi: SensorAPI | null = null
  let onSelect: (selection: Selection) => any = noop
  let inputModality: InputModality = 'mouse'
  const isDraggingStateRef = { current: false }

  function updateHighlightedAttribute(
    item: Element | null | undefined,
    highlight: boolean
  ) {
    item?.setAttribute('data-highlighted', highlight ? 'true' : 'false')
    item?.setAttribute(
      'data-focus-visible',
      highlight && inputModality === 'keyboard' ? 'true' : 'false'
    )
    item?.dispatchEvent(new MouseEvent(highlight ? MOUSE_ENTER : MOUSE_LEAVE))
  }

  function select(
    event:
      | React.MouseEvent<HTMLElement, MouseEvent>
      | React.KeyboardEvent<HTMLElement>
  ) {
    if (ref.current && highlightedItem) {
      const disabled = JSON.parse(highlightedItem?.dataset.disabled || 'false')
      if (disabled) return

      const item = JSON.parse(highlightedItem?.dataset.value || 'null')
      const selected = JSON.parse(highlightedItem?.dataset.selected || 'false')
      const restoreFocus = highlightedItem?.dataset.restorefocus
        ? JSON.parse(highlightedItem?.dataset.restorefocus)
        : undefined

      if (highlightedItem.tagName === 'A') {
        highlightedItem.click()
      }

      onSelect?.({
        event,
        item,
        group: false,
        action: selected ? 'unselected' : 'selected',
        ...(restoreFocus === undefined ? {} : { restoreFocus }),
      })
    }
  }

  function highlightItem(
    item: HTMLElement | null | undefined,
    shouldScroll: boolean = true,
    shouldFocus: boolean = false
  ) {
    // performance improvement, highlightItem is called onMouseMove for Item
    if (item === highlightedItem) {
      return
    }

    if (item) {
      updateHighlightedAttribute(highlightedItem, false)

      // TODO add localized screenreader instructions for the item
      updateHighlightedAttribute(item, true)

      const id = item.getAttribute('id') ?? undefined
      onChangeActiveDescendant(id, ref.current)

      highlightedItem = item

      if (shouldFocus) {
        item.focus()
      }

      if (shouldScroll) {
        scrollIntoView(-1, item)
      }
    } else {
      onChangeActiveDescendant('', ref.current)
    }
  }

  function handleItemBlur(el?: HTMLDivElement) {
    updateHighlightedAttribute(el, false)
    highlightedItem = null
  }

  function getPrevHighlightableItem(
    currentItem: HTMLElement
  ): HTMLElement | null | undefined {
    const prev = getPrevItem(currentItem, ref.current)
    if (prev) {
      if (isItem(prev)) {
        return prev
      }
    } else if (circular) {
      return getLastItem(ref.current)
    }

    return prev
  }

  function getNextHighlightableItem(
    currentItem: HTMLElement
  ): HTMLElement | null | undefined {
    const next = getNextItem(currentItem, ref.current)

    if (next) {
      if (isItem(next)) {
        return next
      }
    } else if (circular) {
      return getFirstItem(ref.current)
    }
  }

  function highlightFirstItem(
    shouldScroll: boolean = true,
    shouldFocus: boolean = true
  ) {
    inputModality = lastDocumentInputType
    highlightItem(getFirstItem(ref.current), shouldScroll, shouldFocus)
  }

  function highlightLastItem(
    shouldScroll: boolean = true,
    shouldFocus: boolean = true
  ) {
    inputModality = lastDocumentInputType
    highlightItem(getLastItem(ref.current), shouldScroll, shouldFocus)
  }

  function highlightPrevItem() {
    inputModality = 'keyboard'
    if (highlightedItem && ref.current?.contains(highlightedItem)) {
      const prev = getPrevHighlightableItem(highlightedItem)
      if (prev) {
        highlightItem(prev, true, true)
      }
    } else {
      highlightLastItem(true, true)
    }
  }

  function highlightNextItem() {
    inputModality = 'keyboard'
    if (highlightedItem && ref.current?.contains(highlightedItem)) {
      const next = getNextHighlightableItem(highlightedItem)
      if (next) {
        highlightItem(next, true, true)
      }
    } else {
      highlightFirstItem(true, true)
    }
  }

  return {
    handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
      if (ref.current) {
        const isDragActive = isDraggingStateRef.current
        if (event.key === 'ArrowUp' || event.key === 'Up') {
          if (!isDragActive) {
            event.preventDefault()
            highlightPrevItem()
          }
        } else if (event.key === 'ArrowDown' || event.key === 'Down') {
          if (!isDragActive) {
            event.preventDefault()
            highlightNextItem()
          }
        } else if (event.key === 'Enter') {
          event.preventDefault()
          if (!isDragActive) {
            select(event)
          }
        }
      }
    },
    handleItemHover(el?: HTMLDivElement) {
      inputModality = 'mouse'
      highlightItem(el, false)
    },
    handleItemFocus(el?: HTMLDivElement) {
      highlightItem(el, true, true)
    },
    handleItemBlur(el?: HTMLDivElement) {
      handleItemBlur(el)
    },
    handleItemsMouseLeave() {
      if (inputModality !== 'mouse' || !highlightedItem) {
        return
      }
      updateHighlightedAttribute(highlightedItem, false)
      highlightedItem = null
      onChangeActiveDescendant('', ref.current)
    },
    useKeyboardSensor(api: SensorAPI) {
      dragAndDropApi = api
    },
    setIsDragging(isDragging: boolean) {
      isDraggingStateRef.current = isDragging
    },

    updateSelectCallback(callback: (selection: Selection) => any) {
      onSelect = callback
    },

    highlight: highlightItem,

    highlighted() {
      return JSON.parse(highlightedItem?.dataset.value || 'null')
    },

    highlightFirst: highlightFirstItem,
    highlightLast: highlightLastItem,
    rehighlightCurrent() {
      // Rehighlights an item that may have lost its styling due to the re-render of the list items
      const draggableId = highlightedItem?.getAttribute('data-rbd-draggable-id')
      if (draggableId) {
        const newItem = ref.current?.querySelector<HTMLElement>(
          `[data-rbd-draggable-id="${draggableId.replace(/["\\]/g, '\\$&')}"]`
        )
        highlightItem(newItem)
      }
    },

    highlightSelected(shouldScroll: boolean = true) {
      const selectedItem = ref.current?.querySelector<HTMLElement>(
        '[data-selected="true"]'
      )

      highlightItem(selectedItem, shouldScroll)
    },

    highlightSuggested(shouldScroll: boolean = true) {
      const suggestedItem = ref.current?.querySelector<HTMLElement>(
        '[data-suggested="true"]'
      )

      highlightItem(suggestedItem, shouldScroll)
    },

    prev: highlightPrevItem,
    next: highlightNextItem,
    select,
  }
}
